/*
 * Copyright (c) 2020. American Express Company
 */

import React from 'react';
import $ from 'jquery';
import _ from 'lodash';
import { ChronoUnit, LocalDate, LocalDateTime } from '@js-joda/core';
import { IconSavings } from '@americanexpress/dls-react';

import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { DebtModel } from 'src/types/debt.model';
import { DialogModel } from 'src/types/dialog.model';
import { DialogTemplateModel } from 'src/types/dialog-template.model';
import { InstitutionModel } from 'src/types/institution.model';
import { InterfaceActionTypeModel } from 'src/types/interface-action-type.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import { RealEstateModel } from 'src/types/real-estate.model';
import { UserActionTypeModel } from 'src/types/user-action.type';
import { FullUserModel, StatusCode } from 'src/types/user.model';
import CardService from '../../services/card.service';
import CashFlowService from '../../services/cashflow.service';
import ConfettiService from '../../services/confetti.service';
import FinanceService from '../../services/finance.service';
import { JaegerService } from '../../services/jaeger.service';
import LiteralsService from '../../services/literals/LiteralsService';
import LogoutService from '../../services/logout.service';
import MixpanelService from '../../services/mixpanel.service';
import NavigationService from '../../services/navigation.service';
import StorageService, { Storage } from '../../services/storage.service';
import TimeService from '../../services/time.service';
import TrackingService from '../../services/tracking.service';
import UtilsService from '../../services/utils.service';
import UserService from '../../services/user.service';

import DragAndDrop from '../../components/mentors/drag-and-drop.gif';
import { AFTER_DELETE } from 'src/utils/constants';
import UnitService from 'src/services/unit.service';
import { DLSSize } from 'src/types/dls-size.enum';

export interface DialogValues {
    mentor: string;
    position: string;
    mode: string;
    mood: string;
    buttons: Array<{ text: string; action: Function }>;
    clickAway: boolean;
    closeAction: UserActionTypeModel;
    hidden: boolean;
}

export default class MentorFactory {
    private retry: number;
    private mentorTimer: number;
    private readonly component: any;

    constructor(component: any) {
        this.component = component;
    }

    public componentWillUnmount(): void {
        if (this.mentorTimer) {
            clearTimeout(this.mentorTimer);
        }
    }

    private static randomMentor(): any {
        return ['jessica', 'walter'][Math.floor(Math.random() * 2)];
    }

    private alreadyShowing(template: DialogTemplateModel): boolean {
        return template.slug === this.component.state.mentorClass;
    }

    public dismissMentors(): void {
        this.component.setState({
            overlay: false,
            mentorAnchor: null,
            mentorOpenPopover: false,
            mentorMode: '',
            mentorClass: '',
            mentorDangerous: false
        });
    }

    private async triggerGenericMentorFinish(
        template: DialogTemplateModel,
        dialogValues: DialogValues,
        dialog?: DialogModel,
        codename?: string
    ): Promise<void> {
        let anchor: HTMLElement = null;

        if (template.anchor) {
            anchor = $(template.anchor)[0]; // TODO: remove this default when we update the seeds

            if (!anchor) {
                // try later later but not forever
                this.retry = this.retry ? this.retry + 1 : 1;

                if (this.retry < 5) {
                    this.mentorTimer = window.setTimeout(
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        async () => await this.triggerGenericMentorFinish(template, dialogValues, dialog, codename),
                        200
                    );
                    return;
                }
            }
        }

        if (!dialogValues.hidden) {
            const body = template.body;

            const newState = {
                dialogId: dialog ? dialog.id : null,
                dialogCodename: codename,
                mentorMentor: dialogValues.mentor,
                mentorAnchor: anchor,
                mentorButtonOneText: _.get(dialogValues, 'buttons[0].text'),
                mentorButtonTwoText: _.get(dialogValues, 'buttons[1].text'),
                mentorButtonOneHandler: _.once(() => _.get(dialogValues, 'buttons[0].action')),
                mentorButtonTwoHandler: _.once(() => _.get(dialogValues, 'buttons[1].action')),
                mentorHeader: template.name,
                mentorBody: body,
                mentorMode: _.get(dialogValues, 'mode'),
                mentorMood: _.get(dialogValues, 'mood'),
                mentorOpenPopover: true,
                mentorPosition: _.get(dialogValues, 'position'),
                mentorState: template.name.includes('danger') ? 'danger' : '',
                mentorImage:
                    (template.image && UtilsService.isBase64(template.image)) ||
                    template.image?.includes('.gif') ||
                    template.image?.includes('.png')
                        ? template.image
                        : null,
                mentorImageHeight: template.imageHeight,
                mentorImageWidth: template.imageWidth,
                mentorClickAway: _.get(dialogValues, 'clickAway'),
                mentorCloseAction: _.get(dialogValues, 'closeAction'),
                mentorIcon: template.icon,
                mentorClass: template.slug,
                mentorInfo: template.info,
                mentorDangerous: template.dangerous,
                mentorX: template.mentorX,
                mentorY: template.mentorY,
                mentorHeight: template.mentorHeight
            };

            if (template.delay) {
                window.setTimeout(() => {
                    this.component.setState(newState);
                }, 1000 * template.delay);
            } else {
                this.component.setState(newState);
            }
        }

        window.setTimeout(() => {
            const button = $('.mentor-button-container .MuiButton-outlinedPrimary');
            if (button) {
                button.focus();
            }
        }, 300);
    }

    private async getValuesFromLocalTemplate(template: DialogTemplateModel): Promise<DialogValues> {
        const slug = template.slug.toLowerCase();
        let position: string;
        let mode: string;
        let mood: string;

        switch (true) {
            case slug.includes('bottom-right'):
                position = 'bottom-right';
                break;
            case slug.includes('bottom-left'):
                position = 'bottom-left';
                break;
            case slug.includes('right'):
                position = 'right';
                break;
            case slug.includes('top-left'):
                position = 'top-left';
                break;
            case slug.includes('left'):
                position = 'left';
                break;
            case slug.includes('top'):
                position = 'top';
                break;
            case slug.includes('center'):
                position = 'center';
                break;
            default:
                position = 'bottom';
                break;
        }

        switch (true) {
            case slug.includes('dialog'):
                mode = 'dialog';
                break;
            default:
                mode = 'popover';
                break;
        }

        switch (true) {
            case slug.includes('smiling'):
            case slug.includes('happy'):
                mood = 'happy';
                break;
            case slug.includes('serious'):
            case slug.includes('concerned'):
                mood = 'concerned';
                break;
            default:
                mood = 'neutral';
                break;
        }

        const userActionTypes: UserActionTypeModel[] = _.get(template, 'userActionTypes').filter(
            (userActionType: UserActionTypeModel) => {
                return userActionType.slug !== 'close-dialog';
            }
        );

        let buttons: Array<{ text: string; action: Function; primary: boolean }> = [];
        userActionTypes.forEach((userActionType: UserActionTypeModel) => {
            switch (true) {
                case userActionType.slug.includes('skip'):
                    buttons.push({
                        text: userActionType.name,
                        action: async () => {
                            this.dismissMentors();
                            if (userActionType.interfaceActionTypes) {
                                for (const interfaceAction of userActionType.interfaceActionTypes) {
                                    await this.processInterfaceAction(interfaceAction);
                                }
                            }
                        },
                        primary: userActionType.primary
                    });
                    break;
                case userActionType.slug.includes('continue'):
                    buttons.push({
                        text: userActionType.name,
                        action: async () => {
                            this.dismissMentors();

                            if (userActionType.interfaceActionTypes) {
                                for (const interfaceAction of userActionType.interfaceActionTypes) {
                                    await this.processInterfaceAction(interfaceAction);
                                }
                            }
                        },
                        primary: userActionType.primary
                    });
                    break;
            }
        });

        if (buttons.length > 1 && !buttons[0].primary) {
            buttons = buttons.reverse();
        }

        const clickAway = template.clickAway;
        const closeAction = template.closeAction;
        const hidden = template.hidden;
        const mentor = template.mentor || MentorFactory.randomMentor();

        return {
            mentor,
            position,
            mode,
            mood,
            buttons,
            clickAway,
            closeAction,
            hidden
        };
    }

    public async processInterfaceAction(interfaceAction: InterfaceActionTypeModel): Promise<void> {
        let startDate: LocalDate;
        const user = this.component.props.user;

        void TrackingService.track({ interface: interfaceAction.slug });
        if (interfaceAction.slug.startsWith('auto-add-')) {
            const goal: string = interfaceAction.slug.replace(/auto-add-/, '').replace(/-/g, ' ');
            startDate = LocalDate.now();
            let endDate: LocalDate = startDate;
            let attributes = {};

            const age: number = user.dob.until(LocalDate.now(), ChronoUnit.YEARS);
            const retire: number = age > 66 ? age + 5 : 67;
            const die: number = age > 84 ? age + 5 : 85;

            switch (goal) {
                case 'end of life':
                    startDate = TimeService.getLocalDateAtAge(user.dob, die);
                    endDate = startDate;
                    break;
                case 'retire':
                    startDate = TimeService.getLocalDateAtAge(user.dob, retire);
                    endDate = startDate;
                    break;
                case 'collect social security':
                    startDate = TimeService.getLocalDateAtAge(user.dob, retire);
                    endDate = startDate;
                    const currentIncome: number = await this.component.getIncome();
                    const annualIncome = currentIncome ? Math.round(currentIncome) : 0;
                    const resp = await FinanceService.retrieveSocialSecurity(startDate, annualIncome);
                    const estimatedSocial = annualIncome ? resp.estimated : 0;
                    attributes = {
                        average_income: annualIncome,
                        estimate_social: estimatedSocial,
                        newAnnualIncome: estimatedSocial * 12
                    };
            }

            let card = _.find(user.cards, { description: goal });

            if (!card) {
                card = await CardService.createCard(
                    {
                        description: goal,
                        startDate,
                        endDate,
                        attributes,
                        accountId: null,
                        startIncomes: [],
                        modifyIncomes: []
                    },
                    user,
                    false,
                    true
                );

                this.component.cardFactory.addCardToUser(card);
            }

            await this.component.cardFactory.handleOpenCard(card);

            switch (goal) {
                case 'end of life':
                    await this.component.handleReloadUser();
            }

            return;
        }

        if (interfaceAction.slug.startsWith('open-drawer-')) {
            const drawer: string = interfaceAction.slug.replace(/open-drawer-/, '');
            switch (drawer) {
                case 'endboarding':
                    await this.component.updateStartStep(null);
                    NavigationService.goTo(this.component.props.history, '/', 'Top drawer').catch(console.error);
                    break;
                case 'match':
                    NavigationService.goTo(
                        this.component.props.history,
                        '/drawer/match',
                        'Employer Matching drawer'
                    ).catch(console.error);
                    break;
                default:
                    NavigationService.goTo(this.component.props.history, `/drawer/${drawer}`).catch(console.error);
                    break;
            }
        }

        if (interfaceAction.slug.startsWith('add-goal-')) {
            let goal: string = interfaceAction.slug.replace(/add-goal-/, '').replace(/-/g, ' ');
            goal = UtilsService.capitalizeFirstLetter(goal); // capitalize first letter

            this.setMentorState({
                state: 'addGoal',
                goal
            });

            return;
        }

        if (interfaceAction.slug.startsWith('view-')) {
            const view: string = interfaceAction.slug.replace(/view-/, '');
            this.component.setState({ view });
            return;
        }

        if (interfaceAction.slug.startsWith('onboarding-step')) {
            const step: string = interfaceAction.slug.split('onboarding-step-').pop();
            this.component.drawerFactory.updateDrawer({ disabled: false });
            this.component.updateStartStep(Number(step));
        }

        if (interfaceAction.slug.startsWith('upsert-')) {
            // perform upsert
            const goalName: string = interfaceAction.slug.split('upsert-').pop();
            const cards: CardModel[] = _.get(this.component.props, 'user.cards');
            const card: CardModel = _.find(cards, {
                description: goalName.replaceAll('-', ' ')
            });
            card
                ? NavigationService.goTo(this.component.props.history, `/card/${card.id}`, 'Open Card drawer').catch(
                      console.error
                  )
                : NavigationService.goTo(
                      this.component.props.history,
                      `/create/goal/${goalName}/1`,
                      `Create Card drawer - ${goalName}`
                  ).catch(console.error);
            return;
        }

        switch (interfaceAction.slug) {
            case 'go-back':
                this.component.drawerFactory.handleBack();
                break;
            case 'unsaved-budget-changes':
                await this.UnsavedBudgetDialog();
                break;
            case 'disable-steps-drawer':
                this.component.drawerFactory.updateDrawer({ disabled: true });
                break;
            case 'continue-adjust-goal':
                this.component.drawerFactory.updateDrawer({ disabled: true });
                break;
            case 'continue-after-adjust-goal':
                this.component.drawerFactory.updateDrawer({ disabled: false });
                break;
            case 'skip-ignore-survey':
                clearTimeout(this.component.surveyTimeout);
                const attributes: { [key: string]: any } = this.component.props.user.attributes;
                attributes.dismissedSurvey = LocalDateTime.now().toJSON();
                await this.component.handleUpdateUserAttributes(attributes);
                break;
            case 'cancel-logout':
                this.component.startLogoutTimers();
                break;
            case 'logout-user':
                LogoutService.handleLogoutRedirect();
                break;
            case 'mentor-dont-save':
                MixpanelService.track('Build Your Plan');
                await this.component.showSignup();
                break;
            case 'mentor-decide-later':
                await this.component.handleReloadUser();
                break;
            case 'mentor-only-manual':
                MixpanelService.track('Explore Your Plan');
                break;
            case 'confetti-mentor':
                await ConfettiService.fire($('.mentor').get(0));
                break;
            case 'clear-mentor-state':
                this.setMentorState({});
                break;
            case 'pay-down-debts':
                // TODO : move this to auto-add above by changing slug
                startDate = LocalDate.now();
                let card = _.find(user.cards, { description: 'pay down debt' });

                if (!card) {
                    card = await CardService.createCard(
                        {
                            description: 'pay down debt',
                            startDate,
                            endDate: startDate,
                            attributes: {},
                            accountId: null,
                            startIncomes: [],
                            modifyIncomes: []
                        },
                        user,
                        false,
                        true
                    );

                    this.component.cardFactory.addCardToUser(card);
                }

                await this.component.cardFactory.handleOpenCard(card);
                break;
            case 'added-everything':
                await this.addedEverything();
                break;
            case 'continue-add-goals':
                this.component.drawerFactory.updateDrawer({ disabled: false });
                this.component.updateStartStep(4);
                break;
            case 'add-step0-goal':
                await this.addStepZeroGoal();
                break;
            case 'connect-your-first-account':
                this.component.handleUpdateUserAttribute('connectFirstAccount', true);
                break;
            case 'delete-account':
                MixpanelService.deleteUser(user);
                await UserService.deleteUser();
                if (AFTER_DELETE) {
                    window.location.href = AFTER_DELETE;
                } else {
                    window.location.href = 'https://www.bodeswell.io/delete-account/';
                }
                break;
            case 'intro-danger-bar':
                // Old comment: don't show the danger bar if the timeline is covered APP-6368
                this.component.handleUpdateUserAttribute('seenDangerBar', true);
                await this.ShowDangerBar();
                break;
            case 'intro-danger-bar2':
                await this.ShowDangerBar2();
                break;
            case 'intro-danger-bar3':
                await this.ShowDangerBar3();
                break;
            case 'open-plan':
                // NB : create a new action if we need to open the plan outside of onboarding
                MixpanelService.track('S0 - Introduce Plan');
                await this.component.showSignup();
                await NavigationService.goTo(this.component.props.history, '/insights', 'Insights drawer');
                break;
            case 'into-bodeswell-1':
                await this.IntoBodeswell1();
                MixpanelService.track('o4 Take Tour');
                break;
            case 'skip-tour':
                this.component.handleClearUserAttribute('hideDangerBar');
                MixpanelService.track('o4 Skip Tour');
                if (_.find(user.rawCashFlow, { dangerZone: true })) {
                    await window.dispatchEvent(new Event('resize'));
                    this.component.handleUpdateUserAttribute('seenDangerBar', true);
                    await this.ShowDangerBar();
                }
                break;
            case 'into-bodeswell-2':
                await this.IntoBodeswell2();
                MixpanelService.track('o4 Tour 1');
                break;
            case 'into-bodeswell-3':
                await this.IntoBodeswell3();
                MixpanelService.track('o4 Tour 2');
                break;
            case 'into-bodeswell-4':
                await this.IntoBodeswell4();
                MixpanelService.track('o4 Tour 3');
                break;
            case 'into-bodeswell-5':
                await this.IntoBodeswell5();
                MixpanelService.track('o4 Tour 4');
                break;
            case 'into-bodeswell-6':
                await this.IntoBodeswell6();
                MixpanelService.track('o4 Tour 5');
                break;
            case 'into-bodeswell-7':
                await this.IntoBodeswell7();
                MixpanelService.track('o4 Tour Complete');
                break;
            case 'first-drag':
                await this.FirstDrag();
                break;
            case 'finish-first-drag':
                this.component.handleClearUserAttribute('startupDragGoal');
                break;
            case 'startup-done':
                this.component.handleClearUserAttribute('hideDangerBar');
                if (_.find(user.rawCashFlow, { dangerZone: true })) {
                    await window.dispatchEvent(new Event('resize'));
                    this.component.handleUpdateUserAttribute('seenDangerBar', true);
                    await this.ShowDangerBar();
                }
                break;
        }

        if (interfaceAction.action) interfaceAction.action();
    }

    public demoKeySequenceListener(event: any): void {
        if (!event.ctrlKey) return;
        if (event.key === 'd') {
            // case sensitive
            // this.triggerWelcomeFlowStepOne();
        } else if (event.key === 'l') {
            const mentorState: any = { state: 'buyHomeDemo' };
            this.setMentorState(mentorState);
            // this.triggerBuyHomeStepTwo();
        }
    }

    public setMentorState(mentorState: any): void {
        StorageService.setString('mentors-state', 'mentors', JSON.stringify(mentorState), Storage.session);
        this.component.setState({
            mentor: mentorState
        });
    }

    public async triggerLocalMentor(codename: string, template: DialogTemplateModel): Promise<void> {
        // already displaying this dialog?
        TrackingService.track({ mentor: template.slug }).catch(console.error);
        if (this.alreadyShowing(template)) return;

        const dialogValues: DialogValues = await this.getValuesFromLocalTemplate(template);

        if (template.interfaceActionType) {
            for (const interfaceAction of template.interfaceActionType) {
                await this.processInterfaceAction(interfaceAction);
            }
        }

        this.retry = 0;
        await this.triggerGenericMentorFinish(template, dialogValues, null, codename);
    }

    public triggerGoalDrawer(goalName: string): void {
        // goalName: 'Take a vacation'
        // goalTarget: '.take-a-vacation-goal'
        let goalTarget: string = goalName.replace(/\s+/g, '-').toLowerCase();
        goalTarget = '.' + goalTarget + '-goal';
        const goalButton = $(goalTarget)[0];

        this.setMentorState({
            state: 'dragGoal',
            goal: goalName
        });

        switch (goalName) {
            case 'Take a vacation':
                this.component.setState({
                    overlay: false,
                    mentorMentor: 'walter',
                    mentorAnchor: goalButton,
                    mentorBody: LiteralsService.getJsxLiteral('Mentors.TakeVacation.body'),
                    mentorButtonOneHandler: _.once(this.dismissMentors.bind(this)),
                    mentorButtonOneText: LiteralsService.getLiteral('Mentors.TakeVacation.buttonOne'),
                    mentorButtonTwoText: null,
                    mentorHeader: LiteralsService.getLiteral('Mentors.TakeVacation.header'),
                    mentorLearnMore: null,
                    mentorMode: 'dialog',
                    mentorMood: 'happy',
                    mentorOpenPopover: true,
                    mentorPosition: 'right',
                    mentorImage: DragAndDrop,
                    mentorImageHeight: 132,
                    mentorImageWidth: 200,
                    mentorClickAway: true
                });
                break;
        }
    }

    public triggerCreateGoal(card: CardModel): void {
        const mentorState = _.get(this.component, 'state.mentor');
        if (mentorState?.goal && mentorState.goal.toLowerCase() !== card.description) {
            this.triggerSaveMentor();
            return;
        }

        if (!mentorState || !mentorState.state) {
            return;
        }

        this.setMentorState({
            state: 'updateGoal',
            goal: card.description
        });

        switch (card.description) {
            case 'take a vacation':
                this.dismissMentors();
                card.startDate = LocalDate.now().plusMonths(2);
                CardService.updateCard(card).catch(console.error);
                break;
        }
    }

    public triggerSaveMentor(): void {
        StorageService.setSavedMentor(this.component.state.mentor);

        this.setMentorState({
            state: null,
            goal: null
        });

        this.component.setState({
            mentorMentor: 'walter',
            mentorAnchor: $('.mentor-button')[0],
            mentorBody: LiteralsService.getJsxLiteral('Mentors.Save.body'),
            mentorButtonOneText: null,
            mentorButtonTwoText: null,
            mentorHeader: null,
            mentorLearnMore: null,
            mentorMode: 'popover',
            mentorMood: 'neutral',
            mentorOpenPopover: true,
            mentorPosition: 'bottom'
        });
    }

    /** Local mentors */

    public async UnsavedBudgetDialog(): Promise<void> {
        const codename: string = 'adjust-retire-top-popover';
        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction1: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction1.slug = 'go-back';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.UnsavedBudget.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'skip-back';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.UnsavedBudget.buttonTwo');
        userActionType2.primary = true;
        userActionType2.slug = 'continue-go-back';
        userActionType2.interfaceActionTypes = [interfaceAction1];

        template.name = LiteralsService.getLiteral('Mentors.UnsavedBudget.header');
        template.slug = 'unsaved-budget-dialog';
        template.body = LiteralsService.getJsxLiteral('Mentors.UnsavedBudget.body');
        template.clickAway = true;
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async FirefoxWarningDialog(): Promise<void> {
        const codename: string = 'firefox-warning-dialog';
        const template: DialogTemplateModel = new DialogTemplateModel();

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.FirefoxWarning.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-dismiss';
        template.name = LiteralsService.getLiteral('Mentors.FirefoxWarning.header');
        template.anchor = '.scroll';
        template.body = LiteralsService.getJsxLiteral('Mentors.FirefoxWarning.body');
        template.clickAway = true;
        template.slug = 'firefox-warning-dialog-template';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public async ShowDangerBar(): Promise<void> {
        // APP-5298: Do not show danger bar when reviewing transactions
        if (window.location.pathname.includes('review')) return;
        const codename: string = 'danger-bar-1-popover';
        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'intro-danger-bar2';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.DangerBar.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-danger-bar2';
        userActionType1.interfaceActionTypes = [interfaceAction];

        template.name = 'danger-bar-1-popover-template';
        const cashFlow: CashFlowModel = _.find(this.component.props.user.cashFlow, (cf) => {
            return cf.dangerZone;
        });
        template.mentorX = cashFlow
            ? UnitService.getJaegerX(this.component.state.jaegerContext, this.component.props.user, cashFlow.startDate)
            : JaegerService.getJaegerWidth() / 2 + JaegerService.getJaegerLeft();
        template.mentorY = JaegerService.getJaegerYTimeline();
        template.mentorHeight = 117;
        template.dangerous = true;
        template.body = LiteralsService.getJsxLiteral('Mentors.DangerBar.body1');
        template.clickAway = true;
        template.slug = 'danger-bar-1-popover-top';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public async ShowDangerBar2(): Promise<void> {
        const codename: string = 'danger-bar-2-popover';
        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'intro-danger-bar3';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.DangerBar.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-danger-bar3';
        userActionType1.interfaceActionTypes = [interfaceAction];

        template.name = 'danger-bar-2-popover-template';
        const cashFlow: CashFlowModel = _.find(this.component.props.user.cashFlow, (cf) => {
            return cf.dangerZone;
        });
        template.mentorX = cashFlow
            ? UnitService.getJaegerX(this.component.state.jaegerContext, this.component.props.user, cashFlow.startDate)
            : JaegerService.getJaegerWidth() / 2 + JaegerService.getJaegerLeft();
        template.mentorY = JaegerService.getJaegerYTimeline();
        template.mentorHeight = 244;
        template.dangerous = true;
        template.body = LiteralsService.getJsxLiteral('Mentors.DangerBar.body2');
        template.clickAway = true;
        template.slug = 'danger-bar-2-popover-top';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public async ShowDangerBar3(): Promise<void> {
        const codename: string = 'danger-bar-3-popover';
        const template: DialogTemplateModel = new DialogTemplateModel();

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.DangerBar.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-danger-bar-done';

        if (this.component.state.startupStep) {
            const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
            interfaceAction.slug = 'mentor-status';
            userActionType1.interfaceActionTypes = [interfaceAction];
        }

        template.name = 'danger-bar-3-popover-template';
        const cashFlow: CashFlowModel = _.find(this.component.props.user.cashFlow, (cf) => {
            return cf.dangerZone;
        });
        template.mentorX = cashFlow
            ? UnitService.getJaegerX(this.component.state.jaegerContext, this.component.props.user, cashFlow.startDate)
            : JaegerService.getJaegerWidth() / 2 + JaegerService.getJaegerLeft();
        template.mentorY = JaegerService.getJaegerYTimeline();
        template.mentorHeight = 160;
        template.dangerous = true;
        template.body = LiteralsService.getJsxLiteral('Mentors.DangerBar.body3');
        template.clickAway = true;
        template.slug = 'danger-bar-3-popover-top';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell1(): Promise<void> {
        const codename: string = 'into-bodeswell-1-dialog';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-2';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.Step1.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-1';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'skip-tour';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.Step1.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-5';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        switch (this.component.props.user.status) {
            case StatusCode.OffTrack:
                template.name = LiteralsService.getLiteral('Mentors.Tour.Step1.OffTrack.header');
                template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step1.OffTrack.body');
                template.icon = <IconSavings size={DLSSize.LARGE} />;
                break;
            case StatusCode.OnTrack:
                template.name = LiteralsService.getLiteral('Mentors.Tour.Step1.OnTrack.header');
                template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step1.OnTrack.body');
                template.icon = <IconSavings size={DLSSize.LARGE} />;
                break;
        }

        template.clickAway = true;
        template.slug = 'into-bodeswell-1-dialog-template-center';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell2(): Promise<void> {
        const codename: string = 'into-bodeswell-2-popover';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-3';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-2';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'startup-done';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-5';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        template.name = 'into-bodeswell-2';
        template.anchor = '.status-drawer-alternate .plan .moreInfoTooltip';
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step2.body');
        template.clickAway = true;
        template.slug = 'into-bodeswell-2-popover-right';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell3(): Promise<void> {
        const codename: string = 'into-bodeswell-3-popover';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-4';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-3';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'startup-done';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-5';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        template.name = 'into-bodeswell-3';
        template.anchor = '.status-drawer-buttons-container .goals-button';
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step3.body');
        template.clickAway = true;
        template.slug = 'into-bodeswell-3-popover-right';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell4(): Promise<void> {
        const codename: string = 'into-bodeswell-4-popover';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-5';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-4';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'startup-done';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-5';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        template.name = 'into-bodeswell-4';
        template.anchor = '.status-drawer-buttons-container .act-button';
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step4.body');
        template.clickAway = true;
        template.slug = 'into-bodeswell-4-popover-right';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell5(): Promise<void> {
        const codename: string = 'into-bodeswell-5-popover';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-6';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-5';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'startup-done';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-5';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        template.name = 'into-bodeswell-5';
        template.anchor = '.status-drawer-buttons-container .insights-button';
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step5.body');
        template.clickAway = true;
        template.slug = 'into-bodeswell-5-popover-right';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell6(): Promise<void> {
        const codename: string = 'into-bodeswell-6-popover';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'into-bodeswell-7';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-6';
        userActionType1.interfaceActionTypes = [interfaceAction];

        const interfaceAction2: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction2.slug = 'startup-done';

        const userActionType2: UserActionTypeModel = new UserActionTypeModel();
        userActionType2.name = LiteralsService.getLiteral('Mentors.Tour.buttonTwo');
        userActionType2.slug = 'continue-startup-done-step-6';
        userActionType2.interfaceActionTypes = [interfaceAction2];

        template.name = 'into-bodeswell-6';
        template.anchor = '.status-drawer-buttons-container .budget-button';
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step6.body');
        template.clickAway = true;
        template.slug = 'into-bodeswell-6-popover-right';
        template.userActionTypes = [userActionType1, userActionType2];

        await this.triggerLocalMentor(codename, template);
    }

    public async IntoBodeswell7(): Promise<void> {
        const codename: string = 'into-bodeswell-7-dialog';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'startup-done';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.Tour.Step7.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-complete-step-7';
        userActionType1.interfaceActionTypes = [interfaceAction];

        template.name = LiteralsService.getLiteral('Mentors.Tour.Step7.header');
        template.body = LiteralsService.getJsxLiteral('Mentors.Tour.Step7.body');
        template.icon = <IconSavings size={DLSSize.LARGE} />;
        template.clickAway = true;
        template.slug = 'into-bodeswell-7-dialog-template-center';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public async FirstDrag(): Promise<void> {
        const codename: string = 'first-drag-popover-top';

        const template: DialogTemplateModel = new DialogTemplateModel();

        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = 'finish-first-drag';

        const userActionType1: UserActionTypeModel = new UserActionTypeModel();
        userActionType1.name = LiteralsService.getLiteral('Mentors.FirstDrag.buttonOne');
        userActionType1.primary = true;
        userActionType1.slug = 'continue-first-drag';
        userActionType1.interfaceActionTypes = [interfaceAction];

        template.mentorX = JaegerService.getJaegerWidth() / 2 + JaegerService.getJaegerLeft();
        template.mentorY = JaegerService.getJaegerYTimeline() - 10;

        template.name = LiteralsService.getLiteral('Mentors.FirstDrag.header');
        template.body = LiteralsService.getJsxLiteral('Mentors.FirstDrag.body');
        template.clickAway = true;
        template.slug = 'first-drag-popover-top-template';
        template.userActionTypes = [userActionType1];

        await this.triggerLocalMentor(codename, template);
    }

    public setRetireAge(goal: string, retireAge: number): void {
        this.component.setState({
            mentor: { ...this.component.state.mentor, goal, onlySelected: true, retireAge }
        });
    }

    private async addedEverything(): Promise<void> {
        const attributes: { [key: string]: any } = this.component.props.user.attributes;

        for (const attribute of [
            'stepZeroIncome',
            'stepZeroExpenses',
            'stepZeroSavings',
            'stepZeroInvestments',
            'stepZeroRInvestments',
            'stepZeroDebts',
            'stepZeroProperty',
            'connectFirstAccount',
            'addingAccounts'
        ]) {
            if (attributes[attribute]) {
                delete attributes[attribute];
                await UserService.deleteAttribute(attribute);
            }
        }
        await this.component.handleUpdateUserAttributes(attributes);
    }

    public moreAccountsBody(): JSX.Element {
        const user: FullUserModel = this.component.props.user;
        const added: any = [];

        const latestInstitution: InstitutionModel = _.maxBy(user.institutions, 'created');
        if (latestInstitution) {
            added.push({
                object: latestInstitution,
                type: 'institution',
                created: latestInstitution.created
            });
        }

        const latestRealEstate: RealEstateModel = _.maxBy(user.realEstate, 'created');
        if (latestRealEstate) {
            added.push({
                object: latestRealEstate,
                type: 'property',
                created: latestRealEstate.created
            });
        }

        const latestHolding: InvestmentHoldingModel = _.maxBy(user.holdings, 'created');
        if (latestHolding) {
            added.push({
                object: latestHolding,
                type: 'assets',
                created: latestHolding.created
            });
        }

        const latestDebt: DebtModel = _.maxBy(_.filter(user.debts, { accountId: null }), 'created');
        if (latestDebt) {
            added.push({
                object: latestDebt,
                type: 'debts',
                created: latestDebt.created
            });
        }

        const latest: any = _.maxBy(added, 'created');

        let body: JSX.Element = null;
        if (!latest) {
            return body;
        }

        const lastTypes: string[] = [];
        let nextType: string = null;

        switch (latest.type) {
            case 'institution':
                const accounts: AccountModel[] = _.filter(user.accounts, (account) => {
                    return account.institution.id === latestInstitution.id;
                });
                if (accounts?.length) {
                    const accountNames = _.map(accounts, 'name');
                    body = LiteralsService.getJsxLiteral('Mentors.MoreAccountsBody.institution', [
                        latestInstitution.name,
                        UtilsService.formatList(accountNames),
                        accounts.length > 1
                    ]);

                    if (_.find(accounts, (account) => account.subtype === 'checking')) lastTypes.push('income');
                    if (_.find(accounts, (account) => account.subtype === 'credit card')) lastTypes.push('expenses');
                    if (_.find(accounts, (account) => account.type === 'loan')) lastTypes.push('debts');
                    if (_.find(accounts, (account) => account.subtype === 'depository')) lastTypes.push('depository');
                    if (_.find(accounts, (account) => account.type === 'investment')) lastTypes.push('assets');
                }
                break;

            case 'debts':
            case 'assets':
            case 'property':
                body = LiteralsService.getJsxLiteral('Mentors.MoreAccountsBody.debtsAssetsProperty', [
                    latest.object.name
                ]);
                lastTypes.push(latest.type);
                break;
        }

        if (!lastTypes.length) {
            return LiteralsService.getJsxLiteral('Mentors.MoreAccountsBody.otherAccounts');
        }

        for (const lastType of _.uniq(lastTypes)) {
            if (this.moreAccountsToAdd(lastType)) {
                return (
                    <span>
                        {body}
                        <br />
                        <br />
                        {MentorFactory.accountsShortMessage(lastType)}
                    </span>
                );
            }
        }

        for (const type of ['income', 'expenses', 'depository', 'assets', 'debts', 'property']) {
            if (this.moreAccountsToAdd(type)) {
                nextType = type;
                break;
            }
        }

        if (!nextType) {
            return (
                <span>
                    {body}
                    <br />
                    <br />
                    {LiteralsService.getLiteral('Mentors.MoreAccountsBody.otherAccounts')}
                </span>
            );
        }

        return (
            <span>
                {body}
                <br />
                <br />
                {MentorFactory.accountsToAddMessages(lastTypes[0], nextType)}
            </span>
        );
    }

    private static accountsToAddMessages(lastType: string, nextType: string): string {
        const returnString: string = LiteralsService.getLiteral('Mentors.AccountsToAdd.accountsMatch', [
            lastType,
            lastType === 'income' || lastType === 'property'
        ]);

        switch (nextType) {
            case 'income':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.income');

            case 'expenses':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.expenses');

            case 'depository':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.depository');

            case 'assets':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.assets');

            case 'debts':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.debts');

            case 'property':
                return returnString + LiteralsService.getLiteral('Mentors.AccountsToAdd.property');
        }
    }

    private static accountsShortMessage(lastType: string): string {
        switch (lastType) {
            case 'income':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.income');

            case 'expenses':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.expenses');

            case 'depository':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.depository');

            case 'assets':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.assets');

            case 'debts':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.debts');

            case 'property':
                return LiteralsService.getLiteral('Mentors.AccountsShortMessage.property');

            default:
                return LiteralsService.getLiteral('Mentors.MoreAccountsBody.otherAccounts');
        }
    }

    private moreAccountsToAdd(type: string): boolean {
        const user: FullUserModel = this.component.props.user;
        const accounts: AccountModel[] = user.accounts;
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());

        switch (type) {
            case 'income':
                const nszIncome = Number(user.attributes.stepZeroIncome);
                if (nszIncome) {
                    let totalIncome: number = 0;
                    for (let i = 1; i < 13; i++) {
                        const cashFlow: CashFlowModel = user.rawCashFlow[offset - i];
                        totalIncome += cashFlow.income;
                    }

                    return totalIncome < 0.8 * nszIncome;
                }
                break;

            case 'expenses':
                const nszExpenses = Number(user.attributes.stepZeroExpenses);
                if (nszExpenses) {
                    let totalExpenses: number = _.sumBy(user.cashFlow, 'expense');
                    for (let i = 1; i < 13; i++) {
                        const cashFlow: CashFlowModel = user.rawCashFlow[offset - i];
                        totalExpenses += cashFlow.expense;
                    }

                    return Math.abs(totalExpenses / 12) < 0.8 * nszExpenses;
                }
                break;

            case 'depository':
                const nszSavings = Number(user.attributes.stepZeroSavings);
                if (nszSavings) {
                    const accounts: AccountModel[] = user.accounts;
                    const savings: AccountModel[] = _.filter(accounts, (account) => account.type === 'depository');
                    const totalSavings = _.sumBy(savings, 'current') + _.sumBy(user.holdings, 'amount');

                    return totalSavings < 0.8 * nszSavings;
                }
                break;

            case 'assets':
                const nszAssets = Number(user.attributes.stepZeroInvestments);
                if (nszAssets) {
                    const accounts: AccountModel[] = user.accounts;
                    const savings: AccountModel[] = _.filter(accounts, (account) => account.type === 'investment');
                    const totalAssets = _.sumBy(savings, 'current') + _.sumBy(user.holdings, 'amount');

                    return totalAssets < 0.8 * nszAssets;
                }
                break;

            case 'debts':
                const nszDebts = Number(user.attributes.stepZeroDebts);
                if (nszDebts) {
                    const loans: AccountModel[] = _.filter(accounts, (account) => account.type === 'loan');
                    const debts: DebtModel[] = _.filter(user.debts, (debt) => !debt.accountId);
                    const totalDebts = _.sumBy(loans, 'current') + _.sumBy(debts, 'amount');

                    return totalDebts < 0.8 * nszDebts;
                }
                break;

            case 'property':
                const nszRealEstate = Number(user.attributes.stepZeroProperty);
                if (nszRealEstate) {
                    const totalRealEstate: number = _.sumBy(user.realEstate, 'amount');

                    return totalRealEstate < 0.8 * nszRealEstate;
                }
                break;
        }

        return false;
    }

    private async addStepZeroGoal(): Promise<void> {
        let card: string = _.get(this, 'component.props.user.attributes.stepZeroGoal');
        if (!card) {
            this.component.updateStartStep(8);
            return;
        }

        let startDate;
        switch (card) {
            case 'save for college':
                card = 'child starts college';
                startDate = startDate = LocalDate.now().withDayOfMonth(1).plusYears(10);
                break;
            case 'retire':
                startDate = this.component.props.user.dob.plusYears(66);
                break;
            case 'buy new home':
                startDate = LocalDate.now().withDayOfMonth(1).plusYears(1);
                break;
            case 'make a large purchase':
                startDate = LocalDate.now().withDayOfMonth(1).plusMonths(6);
                break;
            default:
                startDate = LocalDate.now().withDayOfMonth(1);
        }

        const goal: CardModel = _.find(this.component.props.user.cards, { description: card });
        if (goal) {
            await this.component.cardFactory.handleOpenCard(goal);
        } else {
            await this.component.cardFactory.handleCreateAndOpenCard({ description: card }, startDate);
        }

        // when we set the startup step to the last step (8) we are setting a sort of trigger
        // the next time we go to the top drawer it will turn on the danger bar case, make sure we display this
        // drawer first so we don't trigger it prematurely
        window.setTimeout(() => {
            this.component.updateStartStep(8);
        }, 1000);
    }

    public async processAction(slug: string): Promise<void> {
        const interfaceAction: InterfaceActionTypeModel = new InterfaceActionTypeModel();
        interfaceAction.slug = slug;
        await this.processInterfaceAction(interfaceAction);
    }
}
